import React, { useEffect, useReducer, useRef, useState } from "react";
import { useLocaleContext } from "../../../context/LocaleContext";
import { useUserContext } from "../../../context/UserContext";
import { useModalContext } from "../../../context/ModalContext";
import { useEventContext } from "../../../context/EventContext";
import { usePlayerContext } from "../../../context/PlayerContext";
import { ERROR_MESSAGE } from "../../../constants/common";
import { setFocusToEnd } from "../../../helpers/set-focus-to-end";
import { encodeInnerHTML } from "../../../helpers/html-helpers";
import { MESSAGE_EMOJI_SRC } from "../../../constants/components/emoji";
import UserAuthenticationModal from "../../User/UserAuthenticationModal";
import UserBannedModal from "../../User/UserBannedModal";
import EmojiPicker from "../EmojiPicker";
import { SubmitMessage } from "../../Icons/IconPackage/Message";
import { CButton } from "../../UI";
import UICheckbox from "../../Authentication/InputField/UICheckbox";

import "./style.scss";

const MAX_LENGTH_THRESHOLD_PERCENT = 75;

export default (
    {
        onSubmit,
        onClose,
        parentMessage,
        allowAnonymousMessage = false,
        maxMessageLength = 250,
        mainMessageIdOfThread,
        moduleRef,
        stream,
        anonymous,
        setAnonymous,
        saveMessageText = '',
        onChangeMessageText,
        keyboardShowHandler,
    }) => {

    const { event } = useEventContext();
    const { user } = useUserContext();
    const $locale = useLocaleContext();
    const { renderModal } = useModalContext();
    const { tabletView } = usePlayerContext();
    const [disabled, setDisabled] = useState(false);
    const [text, setText] = useState('');
    const [initHeight, setInitHeight] = useState();
    const [heightIncreased, setHeightIncreased] = useState(false);
    const [isFocused, setFocused] = useState(false);
    const [isEmojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const [contentPickerMeta, updateContentPickerMeta] = useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        {
            top: 0,
            bottom: 0,
            width: 0,
            height: 0,
        },
    );

    const formRef = useRef(null);
    const textInputRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const emojiPickerTriggerRef = useRef(null);
    const textRef = useRef(text || saveMessageText);

    const className = 'comment-message-writeForm';
    const textInputPlaceholder = $locale.get(
        parentMessage
            ? 'comments.writeForm.answer.placeholder'
            : 'comments.writeForm.placeholder'
    );

    useEffect(() => {
        if (textInputRef.current) {
            textInputRef.current.textContent = text || textInputPlaceholder;

            if (onClose) {
                textInputRef.current?.focus();
            }

            if (parentMessage) {
                const text = parentMessage && !parentMessage.anonymous
                    ? `${parentMessage.clientRegistration.nickname ?? parentMessage.client.nickname},\u00a0`
                    : saveMessageText;
                setText(text);
                textInputRef.current.innerText = text;
                setFocusToEnd(textInputRef);
            }

            setInitHeight(textInputRef.current.scrollHeight)
        }

        updateTextInputHeight();

        return () => {
            if (!parentMessage && onChangeMessageText) {
                onChangeMessageText(textRef.current);
            }
        }
    }, []);

    useEffect(() => {
        if (!parentMessage && saveMessageText) {
            setText(saveMessageText);
            textInputRef.current.textContent = saveMessageText;
        }
    }, [saveMessageText]);

    const updateTextInputHeight = () => {
        if (!textInputRef.current) {
            return;
        }

        if (initHeight && textInputRef.current.scrollHeight) {
            setHeightIncreased(textInputRef.current.scrollHeight > (initHeight * 1.5))
        }
        // textInputRef.current.style.height = '5px';
        // textInputRef.current.style.height = textInputRef.current.scrollHeight + 5 + 'px';
    };

    const updateEmojiPickerStyles = () => {
        const containerContent = document.querySelector('#messages-container');

        if (!emojiPickerRef.current || !containerContent) {
            return;
        }

        const containerContentRect = containerContent.getBoundingClientRect();
        const formBlockRect = formRef.current.getBoundingClientRect();

        const emijiPickerHeight = window.innerWidth <= 880 ? (containerContentRect.height - (formBlockRect.height + 14)) : 252;
        const isEmojiPickerPlaced = containerContentRect.top < (formBlockRect.top - emijiPickerHeight) || window.innerWidth <= 880;

        updateContentPickerMeta({
            bottom: isEmojiPickerPlaced ? `${(formBlockRect.height + 4)}px` : 'initial',
            top: isEmojiPickerPlaced ? 'initial' : `${formBlockRect.height - 4}px`,
            width: parentMessage ? '100%' : 'calc(100% - 4px)',
            height: `${emijiPickerHeight}px`,
        });
    };

    const handleChangeText = ({ isChar = true }) => {
        if (isChar) {
            setEmojiPickerOpen(false);
        }

        const encodedInnerHtml = encodeInnerHTML(textInputRef.current.childNodes);
        textRef.current = encodedInnerHtml;
        setText(encodedInnerHtml);
        updateTextInputHeight();
    };

    const handleTextInputKeyUp = (e) => {
        if (e.code === 'Escape') {
            return onClose ? onClose() : handleReset();
        }
    };

    const handleBeforeInput = (e) => {
        if (text.length >= maxMessageLength) {
            e.preventDefault();
        }
    }

    const handlePasteText = (e) => {
        const combinedText = text + e.clipboardData?.getData('text/plain') || '';
        if (combinedText.length > maxMessageLength) {
            const slicedText = combinedText.slice(0, maxMessageLength)
            textInputRef.current.textContent = slicedText
            textInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
            setText(slicedText)
            setFocusToEnd(textInputRef)
        }
    }

    const handleFormKeyUp = (e) => {
        if (e.code === 'Enter' && !(e.ctrlKey || e.shiftKey)) {
            e.preventDefault();
            return handleSubmit();
        }
    };

    const handleFocus = () => {
        if (keyboardShowHandler) {
            keyboardShowHandler();
        }

        if (!text.trim()) {
            textInputRef.current.innerHTML = '';
        }

        setFocused(true);
    };

    const handleBlur = () => {
        if (!text.trim()) {
            textInputRef.current.innerHTML = textInputPlaceholder;
        }

        setFocused(false);
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (disabled) {
            return;
        }

        const isAuth = await new Promise((resolve) => {
            if (!user) {
                renderModal(
                    UserAuthenticationModal,
                    { event, $locale, onClose: (props) => resolve(props?.isAuth) },
                    null,
                    true,
                    moduleRef?.current
                );
            } else {
                resolve(true);
            }
        });

        if (!isAuth) {
            return;
        }

        setEmojiPickerOpen(false);

        const data = {
            text,
            anonymous,
            streamId: stream?.id,
            streamKey: stream?.streamKey,
        };

        if (mainMessageIdOfThread > 0) {
            data.parentId = mainMessageIdOfThread;
        }

        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => formData.append(key, value));

        try {
            setDisabled(true);
            await onSubmit(formData);
            handleReset();
            setDisabled(false);
            if (onClose) {
                onClose();
            }
        } catch (error) {
            setDisabled(false);

            if (error.data?.error?.message === ERROR_MESSAGE.CLIENT_IS_BANNED) {
                renderModal(
                    UserBannedModal,
                    { title: $locale.get('comments.sent.userBan') },
                    null,
                    true,
                );
            }
        }
    };

    const handleReset = () => {
        setText('');
        textRef.current = '';
        setAnonymous(false);
        setFocused(false);
        textInputRef.current.innerHTML = textInputPlaceholder;
        textInputRef.current.blur();
        updateTextInputHeight();
    };

    const handleOpenEmojiPicker = (e) => {
        if (text.length < maxMessageLength) {
            if (!isEmojiPickerOpen) {
                updateEmojiPickerStyles();
            }

            setEmojiPickerOpen(!isEmojiPickerOpen);
        }
    };

    const handleSelectEmoji = (emoji) => {
        textInputRef.current.focus();

        if (text.length >= maxMessageLength) return;

        const image = document.createElement('img');
        image.setAttribute('src', MESSAGE_EMOJI_SRC(emoji.codes.join('_')));
        textInputRef.current.appendChild(image);

        setFocusToEnd(textInputRef);
        handleChangeText({ isChar: false });
    };

    const handleCloseEmojiPicker = (e) => {
        if (!isEmojiPickerOpen || !emojiPickerTriggerRef.current) {
            return;
        }

        if (!(e.target === emojiPickerTriggerRef.current || emojiPickerTriggerRef.current.contains(e.target))) {
            setEmojiPickerOpen(false);
        }
    };

    const showMaxMessageLength = !!text?.length && (heightIncreased || text.length / maxMessageLength * 100 > MAX_LENGTH_THRESHOLD_PERCENT);

    return (
        <div className={`${className} ${isFocused ? `${className}-focused` : ''} ${text ? `${className}-hasText` : ''}`}>
            <form
                ref={formRef}
                onSubmit={handleSubmit}
                onKeyPress={handleFormKeyUp}
            >
                <div className={`${className}-border`}>
                    <div>
                        <div className={`${className}-wrapper`}>
                            <div
                                ref={textInputRef}
                                role="textarea"
                                data-placeholder={textInputPlaceholder}
                                className={`scrollable ${className}-textarea ${!!text.length ? 'active' : ''} ${onClose ? '-sm' : ''}`}
                                style={{ width: '100%' }}
                                disabled={disabled}
                                contentEditable={typeof InstallTrigger !== 'undefined' || "plaintext-only"}
                                spellcheck="false"
                                suppressContentEditableWarning
                                onBeforeInput={handleBeforeInput}
                                onPaste={handlePasteText}
                                onInput={handleChangeText}
                                onKeyUp={handleTextInputKeyUp}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                onTouchStart={handleFocus}
                                onTouchMove={handleBlur}
                            />
                            {onClose && <a onClick={onClose}>{$locale.get('comments.writeForm.cancel')}</a>}
                        </div>
                        <div className={`${className}-controls`}>
                            {!tabletView &&
                                <div ref={emojiPickerTriggerRef} style={{ marginTop: 2 }}>
                                    <CButton
                                        icon="emoji_list"
                                        blank
                                        size="xs"
                                        className={`${className}-emoji-button`}
                                        onClick={handleOpenEmojiPicker}
                                    />
                                </div>
                            }
                            <a onClick={handleSubmit} className={`${className}-submit`}><SubmitMessage /></a>
                            {showMaxMessageLength && <span className="max-message-length">{text.length}/{maxMessageLength}</span>}
                        </div>
                    </div>
                    {!tabletView &&
                        <div
                            ref={emojiPickerRef}
                            style={{ ...contentPickerMeta }}
                            className={`${className}-emoji-picker ${isEmojiPickerOpen ? `${className}-emoji-picker--opened` : ''}`}
                        >
                            <EmojiPicker
                                isOpened={isEmojiPickerOpen}
                                onSelect={handleSelectEmoji}
                                onClose={handleCloseEmojiPicker}
                            />
                        </div>
                    }
                </div>
                {allowAnonymousMessage &&
                    <div className={`${className}-footer`}>
                        <div>
                            {allowAnonymousMessage &&
                                <div className={`${className}-anonymous`}>
                                    <UICheckbox
                                        name="anonymous"
                                        labelClassName={`${className}-anonymous`}
                                        label={$locale.get('comments.writeForm.anonymous')}
                                        value={anonymous}
                                        onChange={(checked) => setAnonymous(checked)}
                                        disabled={disabled}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                }

            </form>
        </div>
    );
}
